/**
 * The query params of a checkout URL that are NOT creator webhook metadata.
 * Shared by the checkout (Checkout.tsx) and its early payment session
 * (checkoutEarlyIntent.ts), which must build the exact same metadata.
 */

// Params the checkout itself consumes: product/pricing selection, embed and
// control flags, and the post-payment navigation params. They must NEVER
// leak into the webhookMetadata creators receive — every metadata builder
// (partner intents incl. discount re-creation, subscription
// create, buy-free) filters the query string through this single set. The
// embed flags a checkout needs after navigation are re-carried explicitly
// (see carriedControlParams).
export const WEBHOOK_EXCLUDED_PARAMS = new Set([
	"productId",
	"subscriptionId",
	"variantId",
	"direct",
	"pw", // payWantPrice
	"c", // payWantCurrency
	"isEmbedded",
	"isDarkMode",
	"isOnWebsite",
	"embedCheckoutOnly",
	"successUrl",
	"cancelUrl",
	"checkout_session_id",
	// Popup embed cache-buster — must never reach creator webhookMetadata (POC-623).
	"_pfcb",
	// Host of the seller page embedding the popup (wallet domain check).
	"parentHost",
	// Prepared ahead of the click by the popup script (prewarmProtocol.ts).
	"prewarm",
	// Overlay embed flag (embed.js).
	"embed",
	// Seats picked on a per-seat subscription (checkoutSeats.ts).
	"seats",
	// The seller's own preview of a draft (ProductSale `?view=true`).
	"view",
	// Tracked link id (Starter `trackedLinks`): sent as `trackedLink`, and
	// credited on the sale itself — not creator metadata.
	"pf_link",
	// NOT excluded on purpose: `customerEmail` / `email` (the popup/session
	// email prefill) and `clientReferenceId` must reach the webhook metadata —
	// they ride the Whop checkout-config metadata and come back on
	// order.completed and every subscription event, so external stores can
	// bind the webhook to the buyer they sent. Only the lock control flag
	// stays out of creator metadata.
	"lockEmail",
	// Personal deadlines: the signed visitor token is the buyer's own — it
	// identifies them to the store and must never reach a creator webhook.
	"pf_sid",
]);

/**
 * Every other query param, serialized as the session's webhook metadata.
 * Same shape the checkout always built (an object, so a repeated key keeps
 * its last value).
 */
export const serializeWebhookMetadata = (searchParams: URLSearchParams) => {
	const customParams: Record<string, string> = {};
	searchParams.forEach((value, key) => {
		if (!WEBHOOK_EXCLUDED_PARAMS.has(key)) {
			customParams[key] = value;
		}
	});
	return Object.entries(customParams)
		.map(
			([key, value]) =>
				`${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
		)
		.join("&");
};
